import { BreakpointObserver, MediaMatcher } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, effect, inject } from '@angular/core';
import { Subscription, of } from 'rxjs';
import { MatSidenav, MatSidenavContent } from '@angular/material/sidenav';
import { CoreService } from 'src/app/services/core.service';
import { AppSettings } from 'src/app/config';
import { filter, switchMap, take } from 'rxjs/operators';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { NavItem } from './vertical/sidebar/nav-item/nav-item';
import { NavService } from '../../services/nav.service';
import { AppNavItemComponent } from './vertical/sidebar/nav-item/nav-item.component';
import { RouterModule } from '@angular/router';
import { MaterialModule } from 'src/app/material.module';
import { CommonModule } from '@angular/common';
import { SidebarComponent } from './vertical/sidebar/sidebar.component';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { TablerIconsModule } from 'angular-tabler-icons';
import { HeaderComponent } from './vertical/header/header.component';
import { AppHorizontalHeaderComponent } from './horizontal/header/header.component';
import { AppHorizontalSidebarComponent } from './horizontal/sidebar/sidebar.component';
import { AppBreadcrumbComponent } from './shared/breadcrumb/breadcrumb.component';
import { AdminNavContextService } from './shared/admin-nav-context.service';
import { CustomizerComponent } from './shared/customizer/customizer.component';
import { IconMenuComponent } from './vertical/sidebar/icon-menu/iconmenu.component';
import { BrandingComponent } from '../full/vertical/sidebar/branding.component';
import { AuthSessionService } from '@core/auth/auth-session.service';
import { AUTH_ENTRY_URL } from '@core/auth/auth-entry.constants';
import { SessionConflictService } from '@core/auth/session-conflict/session-conflict.service';
import { rolesIncludeSuperadmin } from '@core/auth/superadmin-access';
import { AdminShellModeService } from '@core/shell/admin-shell-mode.service';
import {
  PLATFORM_NAV_MODULE_ID,
  POS_RAIL_EXIT_ID,
  POS_RAIL_MODULE_ID_SET,
  POS_RAIL_VENTA_ID,
} from '@core/shell/admin-shell-mode.model';
import { PermissionContextService } from '@core/auth/permission-context.service';
import { StorefrontModulesService } from '@core/storefront/storefront-modules.service';
import { CompanyPlanContextService } from '@core/plan/company-plan-context.service';
import { CompanyPlanSummary } from '@core/plan/company-plan.model';
import { SentryTenantContextService } from '@core/tenant/sentry-tenant-context.service';
import { OrderNotificationsService } from '@core/notifications/order-notifications.service';
import { TenantContextService } from '@core/tenant/tenant-context.service';
import { LocationContextService } from '@core/tenant/location-context.service';
import { OperatedTenantSafetyService } from '@core/tenant/operated-tenant-safety.service';
import { TenantActivationService } from '@core/tenant/tenant-activation.service';
import { AuthService } from '@features/auth/public-api';
import { FinanceCashPreferencesStore } from '@features/finance/public-api';
import { MyPageSetupProgressStore } from '@features/onboard/services/my-page-setup-progress.store';
import { BrowserChromeThemeService } from '@core/browser-chrome/browser-chrome-theme.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SignOutService } from '@core/auth/sign-out.service';
import { LegalPublicService } from '@features/legal/public-api';
import { LegalReconsentDialogComponent } from '@features/legal/public-api';
import {
  buildHorizontalNavItems,
  buildMenuItemsForRoles,
  buildNavItemsForRoles,
  findNavModuleIdForUrl,
  isProfileUtilityUrl,
  moduleHasSubnav,
  PlanModuleGate,
  resolveSelectedNavModuleId,
} from './vertical/sidebar/sidebar-data';
import { OverlayNavDismissDirective } from './overlay-nav-dismiss.directive';
import { isOverlayNavOutsideTarget } from './overlay-nav-dismiss.utils';
import { IconifyLoaderService } from '@core/icons/iconify-loader.service';
import { I18nNamespaceLoader } from '@core/i18n/i18n-namespace.loader';
import { AdminUtilityRailComponent } from './shared/utility-rail/admin-utility-rail.component';
import { MilincoMobileDockComponent } from '@shared/ui/milinco-mobile-dock/milinco-mobile-dock.component';
import { TranslateModule } from '@ngx-translate/core';

const MONITOR_VIEW = 'screen and (min-width: 1024px)';
const BELOWMONITOR = 'screen and (max-width: 1023px)';

// for mobile app sidebar
interface apps {
  id: number;
  icon: string;
  color: string;
  title: string;
  subtitle: string;
  link: string;
}

@Component({
  selector: 'app-full',
  imports: [
    RouterModule,
    AppNavItemComponent,
    MaterialModule,
    CommonModule,
    SidebarComponent,
    NgScrollbarModule,
    TablerIconsModule,
    HeaderComponent,
    AppHorizontalHeaderComponent,
    AppHorizontalSidebarComponent,
    AppBreadcrumbComponent,
    CustomizerComponent,
    IconMenuComponent,
    BrandingComponent,
    OverlayNavDismissDirective,
    AdminUtilityRailComponent,
    MilincoMobileDockComponent,
    TranslateModule,
  ],
  templateUrl: './full.component.html',
  styleUrls: ['./full.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FullComponent implements OnInit, OnDestroy {
  private readonly authSession = inject(AuthSessionService);
  private readonly adminShellMode = inject(AdminShellModeService);
  private readonly permissionContext = inject(PermissionContextService);
  private readonly storefrontModules = inject(StorefrontModulesService);
  private readonly companyPlanContext = inject(CompanyPlanContextService);
  private readonly sentryTenantContext = inject(SentryTenantContextService);
  private readonly tenantContext = inject(TenantContextService);
  private readonly locationContext = inject(LocationContextService);
  private readonly tenantActivation = inject(TenantActivationService);
  private readonly operatedTenantSafety = inject(OperatedTenantSafetyService);
  private readonly browserChrome = inject(BrowserChromeThemeService);
  private readonly sessionConflict = inject(SessionConflictService);
  private readonly orderNotifications = inject(OrderNotificationsService);
  private readonly cashPreferencesStore = inject(FinanceCashPreferencesStore);
  private readonly myPageSetupProgress = inject(MyPageSetupProgressStore);
  private readonly iconifyLoader = inject(IconifyLoaderService);
  private readonly i18nNamespaces = inject(I18nNamespaceLoader);
  private readonly legalPublic = inject(LegalPublicService);
  private readonly adminNavContext = inject(AdminNavContextService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly signOutService = inject(SignOutService);
  private externalClearSubscription = Subscription.EMPTY;
  private legalGateChecked = false;

  navItems = buildNavItemsForRoles([], 'tenant');
  railMenuItems = buildMenuItemsForRoles([], 'tenant');
  /** Memoizado: no regenerar en cada CD (rompe clics del menú horizontal). */
  horizontalNavItems: NavItem[] = buildHorizontalNavItems(this.navItems, this.railMenuItems);

  /** Clave estable para `@for` del sidebar (Angular no permite usar `menus` en `track` del bucle interno). */
  trackSidebarChild(item: NavItem, index: number): string {
    const key = item.route ?? item.navCap ?? item.displayName ?? '';
    return `${key}_${index}`;
  }

  @ViewChild('leftsidenav')
  public sidenav: MatSidenav;
  resView = false;
  /** Sin módulo de rail seleccionado hasta alinear por URL (p. ej. utilidades de perfil). */
  selectedIcon: number | null = null;
  /**
   * Estado overlay del menú para UI inmediata (logo POS).
   * No espera el fin de la animación de MatSidenav.
   */
  mobileNavOpenUi = false;

  @ViewChild('content', { static: true }) content!: MatSidenavContent;
  /** Espejo local del CoreService; se refresca en cada `setOptions`. */
  options = this.settings.getOptions();
  private lastAppliedDir: AppSettings['dir'] | null = null;
  private layoutChangesSubscription = Subscription.EMPTY;
  private isMobileScreen = false;
  private isContentWidthFixed = true;
  private isCollapsedWidthFixed = false;
  private htmlElement!: HTMLHtmlElement;

  get isOver(): boolean {
    return this.isMobileScreen;
  }

  get isTablet(): boolean {
    return this.resView;
  }

  /** Modo shell POS: rail plano sin panel de submenú. */
  isPosShell(): boolean {
    return this.adminShellMode.mode() === 'pos';
  }

  /**
   * Dock inferior handset (admin/taller). No en POS.
   * En ≤1023 el dock sustituye hamburger+drawer aunque la Apariencia sea Horizontal
   * (Horizontal es preferencia de escritorio; en phone no debe reabrir el overlay legacy).
   */
  showMobileDock(): boolean {
    return this.isOver && !this.isPosShell();
  }

  /** POS bajo Ventas conserva el nav tenant, pero usa el lienzo operativo completo. */
  isEmbeddedPos(): boolean {
    const path = this.router.url.split('?')[0];
    return path === '/sales/pos' || path.startsWith('/sales/pos/');
  }

  isPosWorkspace(): boolean {
    return this.isPosShell() || this.isEmbeddedPos();
  }

  /**
   * Preferencia Compacta del customizer (o shell POS): solo rail de iconos.
   * Independiente de si el módulo activo tiene submenú.
   */
  isSidebarMini(): boolean {
    if (this.isPosShell()) {
      return true;
    }
    return (
      this.options.sidenavCollapsed &&
      this.options.navPos !== 'top' &&
      !this.resView
    );
  }

  /**
   * Sin subnavegación (Inicio, hojas): solo rail — escritorio Completa y
   * overlay ≤1023. Evita el hueco blanco del panel vacío.
   * En layout horizontal no aplica (no hay sidenav vertical).
   */
  isDesktopRailOnly(): boolean {
    if (this.isPosShell() || this.options.horizontal) {
      return false;
    }
    if (this.isSidebarMini()) {
      return false;
    }
    return !moduleHasSubnav(this.navItems, this.selectedIcon);
  }

  /**
   * Panel lateral: logo (+ submenú si aplica).
   * Overlay ≤1023 y Completa escritorio: solo con subnavegación real
   * (Inicio/hojas sin hueco vacío; cierra con backdrop o al elegir hoja).
   * Compacta escritorio: nunca.
   */
  showInnerSidebar(): boolean {
    if (this.isPosShell() || isProfileUtilityUrl(this.router.url)) {
      return false;
    }
    if (this.isSidebarMini()) {
      return false;
    }
    return moduleHasSubnav(this.navItems, this.selectedIcon);
  }

  /**
   * Lista de rutas del módulo. Solo con subnavegación real (≥2 rutas).
   * Inicio y hojas: sin lista de una opción.
   */
  showModuleNavList(): boolean {
    if (this.isPosShell() || isProfileUtilityUrl(this.router.url)) {
      return false;
    }
    if (this.selectedIcon == null) {
      return false;
    }
    if (!this.isOver && this.isSidebarMini()) {
      return false;
    }
    return moduleHasSubnav(this.navItems, this.selectedIcon);
  }

  // for mobile app sidebar
  apps: apps[] = [];

  onIconSelected(icon: number) {
    // No adoptar el id de “salir POS” como módulo del panel tenant.
    if (icon === POS_RAIL_EXIT_ID || POS_RAIL_MODULE_ID_SET.has(icon)) {
      if (this.adminShellMode.mode() === 'pos') {
        this.selectedIcon = icon;
      }
      if (this.isPosShell() && this.isOver && this.sidenav?.opened) {
        this.closeOverlayNav();
      }
      this.syncModuleContext();
      return;
    }
    this.selectedIcon = icon;
    // Overlay: cerrar en POS o en módulos hoja (Inicio) — sin panel vacío.
    if (
      this.isOver &&
      this.sidenav?.opened &&
      (this.isPosShell() || !moduleHasSubnav(this.navItems, icon))
    ) {
      this.closeOverlayNav();
    }
    this.syncModuleContext();
  }

  /** Tras Milinco desde POS: panel lateral expandido + módulo según URL (Catálogo). */
  onPosExitCompleted(): void {
    this.openClosedMenu();
    // Esperar rebuild de navItems tenant (effect tras setMode) antes de alinear.
    queueMicrotask(() => {
      const path = this.router.url.split('?')[0] || '/';
      this.adminShellMode.syncModeFromUrl(path);
      this.alignSelectedIconToUrl(path);
    });
  }

  constructor(
    private settings: CoreService,
    private mediaMatcher: MediaMatcher,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private navService: NavService,
    private readonly authService: AuthService
  ) {
    effect(() => {
      const roles = this.authSession.user()?.roles ?? [];
      const mode = this.adminShellMode.mode();
      const loadState = this.permissionContext.loadState();
      this.permissionContext.permissions();
      const can = this.permissionContext.can.bind(this.permissionContext);
      // Siempre leer flags() para que el effect se re-dispare al toggle de Mi página.
      // Antes, si !isLoadedFor se pasaba undefined y effectiveStorefrontModules asumía
      // sede con todo on → Solicitudes seguía en Ventas con servicios apagados.
      const companyId = this.tenantContext.activeCompanyId();
      const storefrontFlags = this.storefrontModules.flags();
      const storefrontModules = companyId != null ? storefrontFlags : undefined;
      const planLoadState = this.companyPlanContext.loadState();
      const planSummary = this.companyPlanContext.summary();
      const planModules = this.resolvePlanModules(planLoadState, planSummary);
      const hidePosTraslado = true;
      const hidePosTurnos = !this.cashPreferencesStore.requireCashShift();
      const hideMyPageChecklist = this.myPageSetupProgress.setupComplete();
      const navOptions = {
        can,
        storefrontModules,
        planModules,
        rolesAdmin: this.companyPlanContext.canShowRolesAdminUi(),
        hidePosTraslado,
        hidePosTurnos,
        hideMyPageChecklist,
      };

      // Platform shell for superadmin does not load a tenant permission map
      // (no active company → loadState stays idle). Still rebuild nav.
      const platformShellReady =
        mode === 'platform' && rolesIncludeSuperadmin(roles);
      if (!platformShellReady && (loadState === 'idle' || loadState === 'loading')) {
        return;
      }
      // Wait for commercial plan so we don't flash “all modules” before gates apply.
      if (
        !platformShellReady &&
        this.tenantHasCompany() &&
        (planLoadState === 'idle' || planLoadState === 'loading')
      ) {
        return;
      }

      this.navItems = buildNavItemsForRoles(roles, mode, navOptions);
      this.railMenuItems = buildMenuItemsForRoles(roles, mode, navOptions);
      this.horizontalNavItems = buildHorizontalNavItems(this.navItems, this.railMenuItems);
      const nextModuleId = resolveSelectedNavModuleId(
        this.navItems,
        this.railMenuItems,
        this.selectedIcon
      );
      if (nextModuleId != null) {
        this.selectedIcon = nextModuleId;
      }
      this.syncModuleContext();
    });

    // Flags sede (catálogo/enlaces/servicios): hidratar tras recarga con companyId en storage.
    effect(() => {
      void this.tenantContext.activeCompanyId();
      this.tenantActivation.ensureStorefrontModulesLoaded();
    });

    // Preferencias cobros/caja de la sede activa (oculta Traslado en modo directa).
    effect(() => {
      void this.tenantContext.activeCompanyId();
      void this.locationContext.activeLocationId();
      const sub = this.cashPreferencesStore.ensureLoaded().subscribe({ error: () => undefined });
      return () => sub.unsubscribe();
    });

    // Setup Mi página: oculta Checklist del rail cuando el onboarding está completo.
    effect(() => {
      void this.tenantContext.activeCompanyId();
      void this.companyPlanContext.loadState();
      void this.companyPlanContext.summary();
      if (!this.tenantHasCompany()) {
        return;
      }
      const sub = this.myPageSetupProgress.ensureLoaded().subscribe({ error: () => undefined });
      return () => sub.unsubscribe();
    });

    // Aviso de sesión única (WhatsApp Web-style): conecta el canal por-usuario en cuanto
    // hay compañía activa; se reconecta si cambia la compañía y se desconecta al cerrar sesión.
    effect(() => {
      const isAuthenticated = this.authSession.isAuthenticated();
      const companyId = this.tenantContext.activeCompanyId();
      if (isAuthenticated && companyId != null) {
        this.sessionConflict.connect(companyId);
      } else {
        this.sessionConflict.disconnect();
      }
    });

    // Aviso de pedidos nuevos (sonido + notificación del navegador + badge en la campanita):
    // misma vida útil que el aviso de sesión única, así notifica sin importar la página activa.
    effect(() => {
      const isAuthenticated = this.authSession.isAuthenticated();
      const companyId = this.tenantContext.activeCompanyId();
      if (isAuthenticated && companyId != null) {
        this.orderNotifications.connect(companyId);
      } else {
        this.orderNotifications.disconnect();
      }
    });

    this.htmlElement = document.querySelector('html')!;

    // Mantener `options` alineado con CoreService (customizer, profile theme, etc.).
    effect(() => {
      const next = this.settings.options();
      const dirChanged =
        this.lastAppliedDir != null && this.lastAppliedDir !== next.dir;
      this.options = next;
      this.receiveOptions(next);
      this.lastAppliedDir = next.dir;
      if (dirChanged) {
        this.restoreDesktopSidenavAfterDirChange();
      }
    });

    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MONITOR_VIEW, BELOWMONITOR])
      .subscribe((state) => {
        this.isMobileScreen = state.breakpoints[BELOWMONITOR];
        this.isContentWidthFixed = state.breakpoints[MONITOR_VIEW];
        this.resView = state.breakpoints[BELOWMONITOR];

        // No mutar `sidenavCollapsed` aquí: Compacta/Completa es preferencia del
        // customizer. Persistir el colapso de tablet pisaba Completa al volver a escritorio.
        this.settings.setOptions({ sidenavOpened: true });

        // Handset + dock: el overlay legacy no debe quedar abierto ni en UI.
        if (this.showMobileDock()) {
          this.mobileNavOpenUi = false;
          if (this.sidenav?.opened) {
            void this.sidenav.close();
          }
        }
      });

    // This is for scroll to top
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.content.scrollTo({ top: 0 });
      });

    // Tras editar Mi página / checklist: re-evaluar si Checklist sigue en el rail.
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const path = event.urlAfterRedirects.split('?')[0];
        if (
          path.startsWith('/storefront/') ||
          path === '/starter' ||
          path.startsWith('/catalog/products')
        ) {
          this.myPageSetupProgress.invalidate();
          this.myPageSetupProgress.ensureLoaded().subscribe({ error: () => undefined });
        }
      });

    // Mantener el rail alineado con la URL (p. ej. /catalog/attributes tras recarga o enlace directo).
    // La URL es fuente de verdad del modo; selectedIcon POS no debe quedar colgado en tenant.
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const path = event.urlAfterRedirects.split('?')[0];
        this.adminShellMode.syncModeFromUrl(path);
        this.scheduleSelectedIconAlignment(path);
      });

    // Cancel/Error (p. ej. canDeactivate false): la URL no cambió — realinear rail/panel.
    // Redirects con UrlTree llegan como NavigationEnd (pin corto en iconmenu, regla 4).
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationCancel | NavigationError =>
            event instanceof NavigationCancel || event instanceof NavigationError
        )
      )
      .subscribe(() => {
        const path = this.router.url.split('?')[0] || '/';
        this.adminShellMode.syncModeFromUrl(path);
        this.scheduleSelectedIconAlignment(path);
      });
  }

  private scheduleSelectedIconAlignment(path: string): void {
    setTimeout(() => this.alignSelectedIconToUrl(path));
  }

  /** Selección de módulo del rail coherente con la URL (y rail tenant tras salir del POS).
   * Debe coincidir con el highlight de `app-iconmenu` (`selectedModuleId` ← `selectedIcon`).
   * Ver docs/DEVELOPMENT.md «Admin shell rail sync».
   */
  private alignSelectedIconToUrl(path: string): void {
    if (isProfileUtilityUrl(path)) {
      this.selectedIcon = null;
      this.syncModuleContext();
      return;
    }
    const moduleId = findNavModuleIdForUrl(this.navItems, path);
    if (moduleId != null) {
      this.selectedIcon = moduleId;
      this.syncModuleContext();
      return;
    }
    if (path.startsWith('/platform')) {
      this.selectedIcon = PLATFORM_NAV_MODULE_ID;
      this.syncModuleContext();
      return;
    }
    if (path === '/pos' || path.startsWith('/pos/')) {
      const posModuleId = findNavModuleIdForUrl(this.navItems, path);
      this.selectedIcon =
        posModuleId != null && POS_RAIL_MODULE_ID_SET.has(posModuleId)
          ? posModuleId
          : POS_RAIL_VENTA_ID;
      this.syncModuleContext();
      return;
    }
    // Tenant (u otra ruta no-POS): si quedó un id del rail POS, resetear al primer módulo válido.
    if (
      this.selectedIcon === POS_RAIL_EXIT_ID ||
      (this.selectedIcon != null && POS_RAIL_MODULE_ID_SET.has(this.selectedIcon))
    ) {
      const nextModuleId = resolveSelectedNavModuleId(
        this.navItems,
        this.railMenuItems,
        this.selectedIcon
      );
      if (nextModuleId != null) {
        this.selectedIcon = nextModuleId;
      }
    }
    this.syncModuleContext();
  }

  /** Eyebrow de page bar: nombre del módulo con subnav (p. ej. Ventas encima de Pedidos). */
  private syncModuleContext(): void {
    const id = this.selectedIcon;
    if (id == null || this.isPosShell()) {
      this.adminNavContext.clear();
      return;
    }
    const rail = this.railMenuItems.find((item) => item.id === id);
    const label = rail?.title?.trim() || null;
    this.adminNavContext.setModuleContext(label, moduleHasSubnav(this.navItems, id));
  }

  ngOnInit(): void {
    this.iconifyLoader.ensureLoaded();
    this.adminShellMode.syncModeFromUrl(this.router.url.split('?')[0]);
    this.warmAdminI18nNamespaces();
    if (this.authSession.isAuthenticated()) {
      this.authService.loadCurrentUser().subscribe({ error: () => {} });
      this.checkPendingLegalConsents();
    }
    // Recarga con tenant operado aún activo: re-mostrar aviso si no hay ack en la pestaña.
    this.operatedTenantSafety.promptEnterAcknowledgement().subscribe();
    document.addEventListener('pointerdown', this.onOverlayNavPointerDownCapture, true);
    // Otra pestaña de este navegador cerró la sesión (logout o conflicto resuelto ahí): seguirla.
    this.externalClearSubscription = this.authSession.externalClear$.subscribe(() => {
      void this.router.navigate(AUTH_ENTRY_URL, { replaceUrl: true });
    });
  }

  /** Gate post-login: reconsent bloqueante; notice como snackbar no bloqueante. */
  private checkPendingLegalConsents(): void {
    if (this.legalGateChecked) {
      return;
    }
    this.legalGateChecked = true;
    this.legalPublic
      .pendingConsents()
      .pipe(take(1))
      .subscribe((pending) => {
        const reconsent = pending.filter((v) => v.changeKind === 'reconsent');
        const notices = pending.filter((v) => v.changeKind === 'notice');
        if (notices.length) {
          const titles = notices.map((n) => n.documentTitle || n.documentKey).join(', ');
          const sessionKey = `legal.notice.${notices.map((n) => n.id).sort().join(',')}`;
          if (!sessionStorage.getItem(sessionKey)) {
            sessionStorage.setItem(sessionKey, '1');
            this.snackBar.open(`Actualización legal: ${titles}`, 'OK', { duration: 12_000 });
          }
        }
        if (!reconsent.length) {
          return;
        }
        const ref = this.dialog.open(LegalReconsentDialogComponent, {
          data: { versions: reconsent },
          disableClose: true,
          width: 'min(480px, 94vw)',
        });
        ref
          .afterClosed()
          .pipe(
            take(1),
            switchMap((action) => {
              if (action === 'logout') {
                this.signOutService.signOut();
                return of(null);
              }
              if (action !== 'accept') {
                return of(null);
              }
              return this.legalPublic.acceptDocuments(
                reconsent.map((v) => ({
                  key: v.documentKey,
                  version: v.version,
                  purpose: 'service',
                  surface: 'admin_reconsent_gate',
                }))
              );
            })
          )
          .subscribe((ok) => {
            if (ok === false) {
              this.snackBar.open('No se pudo registrar la aceptación', 'OK', { duration: 5000 });
              this.legalGateChecked = false;
              this.checkPendingLegalConsents();
            }
          });
      });
  }

  /**
   * Precarga namespaces usados en el menú / POS para que no parpadeen claves
   * (`pos.*`, `sales.*`) mientras llegan los JSON lazy.
   */
  private warmAdminI18nNamespaces(): void {
    const path = this.router.url.split('?')[0] ?? '';
    const base = ['sales', 'pos'] as const;
    const posHeavy =
      path === '/pos' || path.startsWith('/pos/')
        ? (['catalog', 'customers', 'storefront', 'finance'] as const)
        : path === '/sales' || path.startsWith('/sales/')
          ? (['customers', 'storefront'] as const)
          : [];
    this.i18nNamespaces.ensure([...base, ...posHeavy]).subscribe({ error: () => undefined });
  }

  ngOnDestroy() {
    this.layoutChangesSubscription.unsubscribe();
    this.externalClearSubscription.unsubscribe();
    this.sessionConflict.disconnect();
    this.orderNotifications.disconnect();
    document.removeEventListener('pointerdown', this.onOverlayNavPointerDownCapture, true);
  }

  openClosedMenu() {
    // En handset con dock no expandir/abrir el sidenav overlay.
    if (this.showMobileDock()) {
      return;
    }
    this.settings.setOptions({ sidenavCollapsed: false });
    this.resetCollapsedState();
  }

  toggleCollapsed() {
    this.isContentWidthFixed = false;
    this.settings.setOptions({
      sidenavCollapsed: !this.settings.getOptions().sidenavCollapsed,
    });
    this.resetCollapsedState();
  }

  resetCollapsedState(timer = 400) {
    setTimeout(() => this.settings.setOptions(this.settings.getOptions()), timer);
  }

  onToggleMobileNav(): void {
    // Con dock activo el drawer legacy no se monta / no es nav primaria.
    if (this.showMobileDock()) {
      this.closeOverlayNav();
      return;
    }
    if (!this.sidenav) {
      return;
    }
    if (this.isOver) {
      // Estado siguiente ya: ocultar/mostrar logo al clic, no al terminar la animación.
      this.mobileNavOpenUi = !this.sidenav.opened;
    }
    void this.sidenav.toggle();
  }

  /** Cierra el menú overlay (tap fuera, swipe, backdrop). */
  closeOverlayNav(): void {
    this.mobileNavOpenUi = false;
    if (this.sidenav?.opened) {
      void this.sidenav.close();
    }
  }

  /**
   * Capture: el tap en el velo/contenido no debe “atravesar” y activar
   * lo que hay debajo al cerrar el drawer.
   */
  private readonly onOverlayNavPointerDownCapture = (event: PointerEvent): void => {
    if (!this.isOver || !this.sidenav?.opened) {
      return;
    }
    if (!isOverlayNavOutsideTarget(event.target)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.closeOverlayNav();
  };

  onSidenavClosedStart() {
    this.isContentWidthFixed = false;
    if (this.isOver) {
      this.mobileNavOpenUi = false;
    }
  }

  onSidenavOpenedChange(isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isOver;
    // En overlay (móvil) sí persistimos abierto/cerrado.
    // En side (escritorio) Material emite `false` al cambiar `dir` y dejaba el menú muerto hasta F5.
    if (this.isOver) {
      this.settings.setOptions({ sidenavOpened: isOpened });
      this.mobileNavOpenUi = isOpened;
      return;
    }
    this.mobileNavOpenUi = false;
    if (!isOpened) {
      this.settings.setOptions({ sidenavOpened: true });
      this.restoreDesktopSidenavAfterDirChange();
      return;
    }
    this.settings.setOptions({ sidenavOpened: true });
  }

  /**
   * Tras voltear LTR/RTL, Material reposiciona el drawer y a veces lo deja cerrado.
   * Reabrir tras el tick de cambio de `dir` (microtask + timeout por la animación).
   */
  private restoreDesktopSidenavAfterDirChange(): void {
    if (this.isOver || this.resView || this.options.horizontal) {
      return;
    }
    this.settings.setOptions({ sidenavOpened: true });
    const reopen = (): void => {
      if (!this.sidenav || this.isOver || this.resView || this.options.horizontal) {
        return;
      }
      if (!this.sidenav.opened) {
        void this.sidenav.open();
      }
    };
    queueMicrotask(reopen);
    setTimeout(reopen, 50);
  }

  receiveOptions(options: AppSettings): void {
    this.htmlElement.setAttribute('dir', options.dir);
    this.toggleDarkTheme(options);
    this.toggleColorsTheme(options);
    this.toggleCardBorder(options);
  }

  toggleDarkTheme(options: AppSettings) {
    if (options.theme === 'dark') {
      this.htmlElement.classList.add('dark-theme');
      this.htmlElement.classList.remove('light-theme');
    } else {
      this.htmlElement.classList.remove('dark-theme');
      this.htmlElement.classList.add('light-theme');
    }
    this.browserChrome.applyAdmin(options.theme === 'dark');
  }

  /** Sincroniza Apariencia → Tarjetas en `html` (fuente de verdad CSS global). */
  toggleCardBorder(options: AppSettings): void {
    this.htmlElement.classList.toggle('cardBorder', !!options.cardBorder);
  }

  toggleColorsTheme(options: AppSettings) {
    // Remove any existing theme class dynamically
    this.htmlElement.classList.forEach((className) => {
      if (className.endsWith('_theme')) {
        this.htmlElement.classList.remove(className);
      }
    });

    // Add the selected theme class
    this.htmlElement.classList.add(options.activeTheme);
  }

  /** Lee el tenant activo dentro del effect para reactividad. */
  private tenantHasCompany(): boolean {
    return this.tenantContext.activeCompanyId() != null;
  }

  /**
   * Gates comerciales para el rail/sidebar.
   * - Mientras no hay plan: undefined (sin filtrar) — el effect espera loaded.
   * - Error de carga: restrictivo para no mostrar módulos de pago.
   */
  private resolvePlanModules(
    loadState: 'idle' | 'loading' | 'loaded' | 'error',
    summary: CompanyPlanSummary | null
  ): PlanModuleGate | undefined {
    if (loadState === 'error' || (loadState === 'loaded' && !summary)) {
      return {
        moduleMyPage: false,
        moduleLinks: false,
        moduleCatalog: false,
        moduleProducts: false,
        moduleCategories: false,
        moduleAttributes: false,
        allowModifiers: false,
        modulePromotions: false,
        moduleCustomers: false,
        moduleServices: false,
        moduleInventory: false,
        moduleInventoryStock: false,
        moduleInventoryMovements: false,
        moduleInventoryTransfers: false,
        moduleInventoryWarehouses: false,
        moduleFinance: false,
        moduleFinanceTransactions: false,
        moduleFinanceAccounts: false,
        moduleCashCounter: false,
        moduleFinanceCredits: false,
        modulePurchases: false,
        modulePurchasesOrders: false,
        modulePurchasesProviders: false,
        modulePurchasesSupplies: false,
        modulePayroll: false,
        moduleConfiguration: false,
        moduleUsers: false,
        moduleLocations: false,
        moduleSales: false,
        moduleSalesOrders: false,
        modulePos: false,
        modulePosTables: false,
        salesBoard: false,
      };
    }
    if (loadState !== 'loaded') {
      return undefined;
    }
    return {
      moduleMyPage: this.companyPlanContext.moduleMyPageEnabled(),
      moduleLinks: this.companyPlanContext.moduleLinksEnabled(),
      moduleCatalog: this.companyPlanContext.moduleCatalogEnabled(),
      moduleProducts: this.companyPlanContext.moduleProductsEnabled(),
      moduleCategories: this.companyPlanContext.moduleCategoriesEnabled(),
      moduleAttributes: this.companyPlanContext.moduleAttributesEnabled(),
      allowModifiers: this.companyPlanContext.allowModifiersEnabled(),
      modulePromotions: this.companyPlanContext.modulePromotionsEnabled(),
      moduleCustomers: this.companyPlanContext.moduleCustomersEnabled(),
      moduleServices: this.companyPlanContext.moduleServicesEnabled(),
      moduleInventory: this.companyPlanContext.moduleInventoryEnabled(),
      moduleInventoryStock: this.companyPlanContext.moduleInventoryStockEnabled(),
      moduleInventoryMovements: this.companyPlanContext.moduleInventoryMovementsEnabled(),
      moduleInventoryTransfers: this.companyPlanContext.moduleInventoryTransfersEnabled(),
      moduleInventoryWarehouses: this.companyPlanContext.moduleInventoryWarehousesEnabled(),
      moduleFinance: this.companyPlanContext.moduleFinanceEnabled(),
      moduleFinanceTransactions: this.companyPlanContext.moduleFinanceTransactionsEnabled(),
      moduleFinanceAccounts: this.companyPlanContext.moduleFinanceAccountsEnabled(),
      moduleCashCounter: this.companyPlanContext.moduleCashCounterEnabled(),
      moduleFinanceCredits: this.companyPlanContext.moduleFinanceCreditsEnabled(),
      modulePurchases: this.companyPlanContext.modulePurchasesEnabled(),
      modulePurchasesOrders: this.companyPlanContext.modulePurchasesOrdersEnabled(),
      modulePurchasesProviders: this.companyPlanContext.modulePurchasesProvidersEnabled(),
      modulePurchasesSupplies: this.companyPlanContext.modulePurchasesSuppliesEnabled(),
      modulePayroll: this.companyPlanContext.modulePayrollEnabled(),
      moduleConfiguration: this.companyPlanContext.moduleConfigurationEnabled(),
      moduleUsers: this.companyPlanContext.moduleUsersEnabled(),
      moduleLocations: this.companyPlanContext.moduleLocationsEnabled(),
      moduleSales: this.companyPlanContext.moduleSalesEnabled(),
      moduleSalesOrders: this.companyPlanContext.moduleSalesOrdersEnabled(),
      modulePos: this.companyPlanContext.modulePosEnabled(),
      modulePosTables: this.companyPlanContext.modulePosTablesEnabled(),
      salesBoard: this.companyPlanContext.salesBoardEnabled(),
    };
  }
}
